<template>
	<div id="app">
		<header class="header-main" :class="{ 'header-hidden': $route.meta.hide_header }">
			<span id="headerLeft">
				<a class="no-decoration" href="/">
					<img src="./assets/img/gleebeam-logo.svg" alt="Gleebeam logo" />
					<span class="text-logo ml-8">Gleebeam</span>
				</a>
			</span>
			<span id="headerRight" class="hidden-mobile">
				<nav role="navigation">
					<router-link
						to="/dashboard"
						class="button-link"
						:class="{ active: $route.name === 'dashboard' }"
					>
						<img
							src="./assets/img/icons/header/round-dashboard-24px.svg"
							alt="Gleebeam dashboard link icon"
						/>
						<span>Dashboard</span>
					</router-link>
					<router-link
						to="/templates"
						class="button-link ml-24"
						:class="{ active: $route.name === 'templates' }"
					>
						<img
							src="./assets/img/icons/header/round-developer_board-24px.svg"
							alt="Gleebeam templates link icon"
						/>
						<span>Templates</span>
					</router-link>
					<Separator vertical />
					<Select id="userDropdown" v-if="user" :menu_offset="{ top: 64 }">
						<template v-slot:trigger>
							<span id="userDropdownTrigger">
								<img class="user-avatar" :src="user.image_url" alt="User profile image" />
								<span>{{ user.name }}</span>
								<img src="./assets/img/icons/header/chevron-down.svg" alt="User drop-down menu arrow" />
							</span>
						</template>
						<template v-slot:options>
							<!-- <router-link to="/docs">Documentation</router-link> -->
							<a
								href="https://www.notion.so/pizzabreakfast/Gleebeam-docs-c7ae91ccf40d42b2a38fa90e9e16afd3"
								target="_blank"
							>Documentation</a>
							<div @click="pricing_modal_data.active = true">Pricing plans</div>
							<div @click="dashboard_link_modal.active = true">Connect TV</div>
							<div @click="signOut">Sign out</div>
						</template>
					</Select>
				</nav>
			</span>
			<span id="hamburgerMenuToggle">
				<img
					v-if="!menu.open"
					src="./assets/img/icons/header/icon-hamburger.svg"
					@click="menu.open = true"
					alt="Mobile menu icon"
				/>
				<img
					v-else
					src="./assets/img/icons/round-close-24px.svg"
					@click="menu.open = false"
					alt="Mobile menu close button"
				/>
			</span>
			<span id="hamburgerMenu" v-if="menu.open">
				<nav role="navigation">
					<router-link to="/dashboard" class="nav-link" :class="{ active: $route.name === 'dashboard' }">
						<span>Dashboard</span>
					</router-link>
					<router-link to="/templates" class="nav-link" :class="{ active: $route.name === 'templates' }">
						<span>Templates</span>
					</router-link>
					<button class="nav-link" @click="pricing_modal_data.active = true">Pricing plans</button>
					<button to="/" class="nav-link" @click="signOut">Sign out</button>
				</nav>
			</span>
		</header>
		<router-view
			:style="{ 'padding-top': $route.meta.hide_header ? '0' : '72px' }"
			@refreshNotificationData="loadNotificationData"
			@showPaymentForm="showPaymentForm($event.plan)"
			@showPricingPlans="pricing_modal_data.active = true"
			@showDashboardLinkModal="dashboard_link_modal.active = true"
			@showOnboardingTooltip="showOnboardingTooltip($event)"
			@dismissOnboardingTooltip="dismissOnboardingTooltip"
			@updateUserData="updateUserData(user.uid, $event)"
		/>
		<div id="updateNotification" v-if="update.available">
			<span>Update available.&nbsp;</span>
			<button class="button-link primary" @click="reload">Reload</button>
			<span>&nbsp;/&nbsp;</span>
			<button class="button-link primary" @click="update.available = false">Dismiss</button>
		</div>
		<!-- Pricing tier overlay -->
		<transition name="fade">
			<PricingModal
				v-if="pricing_modal_data.active"
				@showPaymentForm="showPaymentForm($event.plan)"
				@dismiss="pricing_modal_data.active = false"
			/>
		</transition>
		<!-- Payment form overlay -->
		<transition name="fade">
			<PaymentForm
				v-if="payment_form_data.active"
				:plan="payment_form_data.plan"
				@dismiss="payment_form_data.active = false"
			/>
		</transition>
		<!-- Dashboard link form overlay -->
		<transition name="fade">
			<DashboardLinkModal
				v-if="dashboard_link_modal.active"
				@dismiss="dashboard_link_modal.active = false"
			/>
		</transition>
		<!-- Onboarding tooltip -->
		<transition name="fade">
			<OnboardingTooltip
				v-if="onboarding_tooltip.active"
				@dismiss="dismissOnboardingTooltip"
				:elementReference="onboarding_tooltip.elementReference"
			>
				<p>
					Congratulations on creating your first notification template! 🥳
					<br />
					<br />
					<b>Click it</b> to learn how to automate it.
				</p>
			</OnboardingTooltip>
		</transition>
	</div>
</template>

<script>
import Notification from "./modules/notification";
import Select from "./components/input/select";
import Separator from "./components/separator/separator";
import PricingModal from "./components/overlay/pricing-modal";
import PaymentForm from "./components/overlay/payment-form";
import DashboardLinkModal from "./components/overlay/dashboard-link-modal";
import OnboardingTooltip from "./components/overlay/onboarding-tooltip";

export default {
	name: "app",
	data() {
		return {
			loading: {
				auth: false
			},
			userAuthenticated: false,
			unsubscribe_notification_updates: null,
			unsubscribe_claims_updates: null,
			unsubscribe_software_updates: null,
			update: {
				available: false,
				version: ""
			},
			menu: { open: false },
			payment_form_data: {
				plan: "",
				active: false
			},
			pricing_modal_data: {
				active: false
			},
			dashboard_link_modal: {
				active: false
			},
			onboarding_tooltip: {
				active: false,
				elementReference: null,
				ondismiss: null
			}
		};
	},
	components: {
		Select,
		Separator,
		PricingModal,
		PaymentForm,
		DashboardLinkModal,
		OnboardingTooltip
	},
	computed: {
		user() {
			return this.$store.getters.user;
		}
	},
	watch: {
		$route(to) {
			this.menu.open = false;

			if (to.matched.some(record => record.meta.load_notification_data)) {
				this.unsubscribeFromNotificationUpdates();
				this.loadNotificationData(
					to.meta.subscribe_to_notification_updates
				);
			} else {
				this.unsubscribeFromNotificationUpdates();
			}

			if (this.$store.getters.paymentScheduled) {
				this.payment_form_data.plan = this.$store.getters.scheduledPaymentPlan;
				this.payment_form_data.active = true;
				this.$store.dispatch("clearPaymentFormSchedule");
			}

			this.$analytics.logEvent("screen_view", {
				screen_name: to.name
			});
		}
	},
	methods: {
		reload() {
			location.reload(true);
		},
		async authStateHandler(user) {
			if (user && !user.isAnonymous) {
				this.userAuthenticated = true;

				await this.loadUserData(user);

				console.info("Signed in as " + user.displayName); // eslint-disable-line no-console

				if (this.$route.meta.load_notification_data) {
					this.unsubscribeFromNotificationUpdates();
					await this.loadNotificationData(
						this.$route.meta.subscribe_to_notification_updates
					);
				}

				this.loading.auth = false;

				if (!this.unsubscribe_software_updates)
					this.unsubscribe_software_updates = this.subscribeSoftwareUpdates();

				this.unsubscribe_claims_updates = this.$db()
					.collection("users")
					.doc(user.uid)
					.collection("metadata")
					.doc("claims")
					.onSnapshot(this.refreshClaims);
			} else {
				this.$store.commit("clearUserData");

				this.userAuthenticated = false;

				window.location.pathname = "/";
			}
		},
		async signOut() {
			// unsubscribe db listeners here?
			try {
				if (this.unsubscribe_claims_updates)
					this.unsubscribe_claims_updates();

				if (this.unsubscribe_software_updates)
					this.unsubscribe_software_updates();

				this.$store.commit("unsubscribeListeners");

				await this.$auth().signOut();
			} catch (error) {
				throw new Error(error.message);
			}
		},
		async loadUserData(authData) {
			let userData = null;

			// load current user data
			const response = await this.$db()
				.collection("users")
				.doc(authData.uid)
				.get();

			// if user already registered
			if (response.exists && response.data().uid)
				userData = response.data();
			// create new user if not registered
			else {
				userData = {
					uid: authData.uid,
					customer_id: null,
					name: authData.displayName,
					image_url: authData.photoURL,
					email: authData.email,
					ga_client_id: window.ga.getAll()[0].get("clientId")
				};

				Object.keys(userData).forEach(key => {
					if (userData[key] === undefined) userData[key] = null;
				});

				await this.updateUserData(userData.uid, userData);
				this.$analytics.logEvent("sign_up", { method: "google" });
			}

			this.$store.commit("setUserData", userData);
			this.$ga.set("userId", userData.uid);
			this.$analytics.logEvent("login", { method: "google" });

			// for users who have registered before ga client ids were collected
			if (!this.user.ga_client_id && !window.webpackHotUpdate)
				this.updateUserData(this.user.uid, {
					ga_client_id: window.ga.getAll()[0].get("clientId")
				});
		},
		async updateUserData(uid, data) {
			await this.$db()
				.collection("users")
				.doc(uid)
				.set(data, { merge: true });

			this.$store.commit("setUserData", data);

			// eslint-disable-next-line no-console
			console.info("User data updated.");
		},
		async refreshClaims() {
			this.$auth().currentUser.getIdToken(true);

			const tokenResult = await this.$auth().currentUser.getIdTokenResult(
				true
			);

			await this.loadUserPlanData(tokenResult.claims.plan);

			this.$store.commit("stopLoading", tokenResult.claims.plan);
		},
		async loadUserPlanData(plan) {
			const planDoc = await this.$db()
				.collection("membership_tiers")
				.doc(plan)
				.get();
			const plan_data = planDoc.data();

			this.$store.commit("setUserData", { plan, plan_data });
			// eslint-disable-next-line no-console
			console.info("Plan data updated.");
		},
		async loadNotificationData(subscribeToUpdates) {
			const ref = await this.$db()
				.collection("notifications")
				.where("author", "==", this.user.uid);

			if (subscribeToUpdates) {
				this.unsubscribe_notification_updates = ref.onSnapshot(
					this.updateNotifications
				);

				this.$store.commit(
					"addListener",
					this.unsubscribe_notification_updates
				);
			} else {
				const data = await ref.get();
				this.updateNotifications(data);
			}
		},
		updateNotifications(snapshot) {
			let notifications = [];

			snapshot.forEach(doc => {
				let notification = new Notification({
					...doc.data(),
					id: doc.id
				});

				notification.initializeLayerData();

				notification = this.migrateNotificationTemplateData(
					notification
				);

				notifications.push(notification);
			});

			notifications.sort((a, b) => b.date_created - a.date_created); // newest first

			this.$store.commit("setNotificationData", notifications);

			console.info("Notification data updated"); // eslint-disable-line no-console
		},
		migrateNotificationTemplateData(notification) {
			// if layers don't have grid positions
			if (!notification.template.every(layer => layer.style.position)) {
				notification.template.forEach((layer, ii) => {
					const colStart = 1 + (ii % 6);
					const rowStart = 1 + Math.floor(ii / 7);

					layer.style.position = {
						column: {
							start: colStart,
							end: colStart + 1
						},
						row: {
							start: rowStart,
							end: rowStart + 1
						}
					};
				});
			}

			return notification;
		},
		unsubscribeFromNotificationUpdates() {
			if (this.unsubscribe_notification_updates) {
				this.$store.commit(
					"removeListener",
					this.unsubscribe_notification_updates
				);
				this.unsubscribe_notification_updates();

				console.info("Unsubscribed notification data updates"); // eslint-disable-line no-console
			}
		},
		subscribeSoftwareUpdates() {
			return this.$db()
				.collection("app_data")
				.doc("updates")
				.onSnapshot(this.checkForUpdate);
		},
		isMobile() {
			if (
				/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
					navigator.userAgent
				)
			) {
				return true;
			} else return false;
		},
		async checkForUpdate(snapshot) {
			let result = false;

			const currentVersion = require("../package.json").version;
			const latestStableVersion = snapshot.data().version_stable;

			const currentNumbers = currentVersion
				.split(".")
				.map(num => parseInt(num));
			const latestNumbers = latestStableVersion
				.split(".")
				.map(num => parseInt(num));

			if (currentNumbers.length === latestNumbers.length) {
				for (let ii = 0; ii < currentNumbers.length; ii++) {
					if (latestNumbers[ii] < currentNumbers[ii]) {
						result = false;
						break;
					} else if (latestNumbers[ii] > currentNumbers[ii])
						result = true;
				}
			}

			this.update.version = latestStableVersion;
			this.update.available = result;
		},
		showPaymentForm(planName) {
			this.payment_form_data.plan = planName;
			this.payment_form_data.active = true;
		},
		showOnboardingTooltip({ elementReference, ondismiss }) {
			this.onboarding_tooltip.elementReference = elementReference;
			this.onboarding_tooltip.ondismiss = ondismiss;

			this.onboarding_tooltip.active = true;
		},
		dismissOnboardingTooltip() {
			this.onboarding_tooltip.active = false;

			if (this.onboarding_tooltip.ondismiss)
				this.onboarding_tooltip.ondismiss();
		}
	},
	async created() {
		this.$auth().onAuthStateChanged(this.authStateHandler);

		const user = this.$auth().currentUser;

		if (user) {
			this.userAuthenticated = true;
			await this.loadUserData(user);
		}
	},
	mounted() {
		this.$analytics.logEvent("screen_view", {
			screen_name: this.$route.name
		});
	}
};
</script>

<style>
@import "./style/defaults.css";
@import "./style/fonts-global.css";
@import "./style/fonts-editor.css";
@import "./style/buttons.css";
@import "./style/input.css";
@import "./style/spacing.css";
@import "./style/vue-transitions.css";
@import "./style/cards.css";
@import "./style/text.css";
@import "./style/layout.css";
@import "./style/navigation.css";

html,
body {
	margin: 0;
	color: var(--lighter-black);
}

* {
	font-family: "Nunito", sans-serif;
	box-sizing: border-box;
}

body {
	background-color: var(--background-white);
}

#app {
	position: relative;
	min-height: 100vh;
	overflow-x: hidden;
}

header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	height: 72px;
	padding: 16px 24px;
	background-color: rgba(255, 255, 255, 0.95);
}

.header-main {
	position: fixed;
	top: 0;
	left: 0;
	z-index: 1;
}

.header-hidden {
	display: none;
}

#headerRight .nav-link {
	margin-right: 32px;
}

#headerRight > *,
#headerLeft > * {
	display: flex;
	align-items: center;
	justify-content: center;
}

#userDropdown {
	position: relative;
	display: inline-block;
}
#userDropdownTrigger {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.user-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
}

#userDropdownTrigger > img:first-child {
	margin-right: 8px;
}

#userDropdownTrigger > img:last-child {
	margin-left: 4px;
}

#updateNotification {
	position: fixed;

	display: flex;
	align-items: center;

	bottom: 0;
	padding: 0 8px;

	background-color: var(--light-blue);
	border-top-right-radius: 4px;
}

.hidden-mobile {
	display: auto;
}

#hamburgerMenu,
#hamburgerMenuToggle {
	display: none;
}

@media screen and (max-width: 1125px) {
	.hidden-mobile {
		display: none;
	}

	#hamburgerMenu {
		position: fixed;
		display: block;

		top: 72px;
		left: 0;
		width: 100%;

		padding: 48px 16px;

		background-color: white;
		box-shadow: 0px 8px 8px -8px rgba(0, 0, 0, 0.16);
	}

	#hamburgerMenu > nav {
		display: grid;
		grid-row-gap: 32px;

		text-align: center;

		align-items: center;
	}

	#hamburgerMenuToggle {
		display: block;
	}
}
</style>
